import React, { useMemo } from 'react';
import { withCookies } from 'react-cookie';
import { useSelector } from 'react-redux';
import { isLogin } from 'store/selectors/loginState';
import { useLocation } from 'react-router-dom';
import { removeLSItem, setLSItem } from 'util/localStorageProvider';
import { HOME_PATH } from 'shared/routes';
import useSessionInvitation from '../hooks/useSessionInvitation';
import InvitationIntro from '../components/InvitationIntro';
import { apiAcceptInvitation, getAwaitingInvitations } from '../api';
import ModalInvitations from '../components/Modalnvitations';

const useFetchAwaitingInvitations = () => {
  const [awaitingInvitation, setAwaitingInvitation] = React.useState({
    data: [],
    loading: false,
    error: '',
  });

  const fetch = async () => {
    const nextState = {
      data: [],
      loading: true,
      error: '',
    };
    try {
      setAwaitingInvitation(nextState);
      const { data: response, errorObj } = await getAwaitingInvitations();

      if (errorObj) {
        throw errorObj;
      }
      setAwaitingInvitation({
        ...nextState,
        loading: false,
        data: response?.data,
      });
      return response?.data;
    } catch (error) {
      setAwaitingInvitation({
        ...nextState,
        loading: false,
        error: error.message,
      });
      throw error;
    }
  };

  return React.useMemo(() => {
    return {
      awaitingInvitation,
      fetch,
    };
  }, [awaitingInvitation]);
};

const SupplierInvitationTrackingContext = React.createContext();

const SupplierInvitationTrackingProvider = ({ children, cookies }) => {
  const isShowedRef = React.useRef(false);
  const location = useLocation();
  const invitation = useSessionInvitation();
  const isLoggedIn = useSelector(isLogin);
  const haveToken = !!cookies.get('token');
  const [openInvitations, setOpenInvitations] = React.useState(false);
  const [showIntro, setShowIntro] = React.useState(false);
  const { awaitingInvitation, fetch } = useFetchAwaitingInvitations();
  const userLoggedIn = useMemo(() => {
    return isLoggedIn && haveToken;
  }, [isLoggedIn, haveToken]);

  const shouldAutoAccept = useMemo(() => {
    return userLoggedIn && !!invitation.invitationCode && !invitation.existed;
  }, [userLoggedIn, invitation]);

  const shouldManualAccept = useMemo(() => {
    return userLoggedIn && !!invitation.invitationCode && invitation.existed;
  }, [userLoggedIn, invitation]);

  const resetTrackingState = () => {
    isShowedRef.current = false;
    fetch();
  };

  const acceptInvitation = async () => {
    if (!invitation.accepted) {
      setLSItem(
        'invitation',
        JSON.stringify({
          ...invitation,
          accepted: true,
        })
      );
      await apiAcceptInvitation([invitation.invitationCode]);
    }
  };

  const acceptBulkInvitation = async (invitationCodes) => {
    await apiAcceptInvitation(invitationCodes);
    setShowIntro(true);
  };

  const context = useMemo(() => {
    return {
      invitation,
      showIntro,
      awaitingInvitation,
      fetchAwaitingInvitation: fetch,
      resetTrackingState,
    };
  }, [invitation, showIntro, awaitingInvitation, fetch]);

  React.useEffect(() => {
    if (shouldAutoAccept) {
      acceptInvitation();
      setShowIntro(shouldAutoAccept);
    }
  }, [shouldAutoAccept, openInvitations]);

  React.useEffect(() => {
    if (!awaitingInvitation.loading && !shouldAutoAccept && !isShowedRef.current) {
      isShowedRef.current = Array.isArray(awaitingInvitation.data)
        ? awaitingInvitation.data.length > 0
        : false;
      setOpenInvitations(
        Array.isArray(awaitingInvitation.data) ? awaitingInvitation.data.length > 0 : false
      );
    }
  }, [awaitingInvitation]);

  React.useEffect(() => {
    // View mounted.
    if (userLoggedIn) {
      fetch();
    }
  }, [userLoggedIn]);

  return (
    <SupplierInvitationTrackingContext.Provider value={context}>
      {children}
      <InvitationIntro
        // invitationName={invitation.companyName}
        open={showIntro && location.pathname === HOME_PATH}
        onSubmit={() => {
          removeLSItem('invitation');
          setShowIntro(false);
        }}
      />
      <ModalInvitations
        open={openInvitations}
        loading={awaitingInvitation.loading}
        data={awaitingInvitation.data}
        fetch={fetch}
        acceptInvitation={acceptBulkInvitation}
        onClose={() => {
          // When Popup Close Remove Session Invitations.
          removeLSItem('invitation');
          setOpenInvitations(false);
        }}
      />
    </SupplierInvitationTrackingContext.Provider>
  );
};

export const useSupplierInvitationTrackingContext = () => {
  return React.useContext(SupplierInvitationTrackingContext);
};

export default withCookies(SupplierInvitationTrackingProvider);
